/**
 * Logic Decomposer — Portfolio Disposition Dashboard (v3).
 *
 * Built to "Logic Decomposer v3 - Dev Handoff.md" and the v3 design component. Views share a
 * persistent side panel: Portfolio, Framework Mapping, Review Queue, Consolidation, Rule
 * Configuration, Engagement Phases.
 *
 * INERT: this module is not routed or mounted anywhere yet, and renders ./fixtures. When the
 * §4 endpoints land, `data` props take over and the fixtures become the fallback.
 *
 * Handoff §3 used to say: "Removed vs v2 (intentional): row checkboxes, Approve / Reassign
 * buttons, bulk selection. Stemmler is the consultant — SMBC actions on their own systems. LD is
 * a read/analyze surface, not a work-assignment tool." Item 11 (Asana 1216373781992284, Ryan
 * 2026-07-30) corrects that note: it predates the six-outcome framework, and it contradicts the
 * disposition spec itself (§5.8, §10.3), which requires consultant confirmation to be bulk —
 * "review AI recommendations, approve/override in batch, then commit." "AI recommends, human
 * confirms" doesn't close without a commit step. The Review Queue view below (framework/
 * ReviewQueue.tsx) is that bulk approve/override/commit surface — Stemmler-side, same as every
 * other write in this module (proposing/publishing a ruleset, overriding a survivor or a box).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutGrid, Grid, Combine, Milestone, Upload, SlidersHorizontal, ListChecks, ClipboardList, History, Loader2 } from 'lucide-react';
import { color, font, outcomeFromRaw, OUTCOME_TO_LEGACY } from './tokens';
import type { View, MainTab, PanelTab, TranslateTab, ArtifactDetail, ArtifactRow, Translation } from './types';
import type { Complexity } from './tokens';
import { artifactDetails, phases as phaseFixtures } from './fixtures';
import { useFrameworkData } from './framework/useFrameworkData';
// Item 14 — the Deliverables tab's live data (replaces the phases fixture below once loaded).
import { useEngagementArtifacts } from './useEngagementArtifacts';
import { useArtifactAnalyze, listDecomposedRuns, loadDecomposedRun } from './useArtifactAnalyze';
import type { IngestProgress, DecomposedRun } from './useArtifactAnalyze';
import { translateDetail, reviewDetail, persistTranslation } from './logicTranslate';
import { useProject } from '../../contexts/ProjectContext';
import ClientTagPicker from '../ClientTagPicker';
import { recordsToArtifactList, recordToArtifactDetail } from './recordsToPortfolio';
import { takePendingLdFiles, isLdParsable, LD_ACCEPT_WITH_COMING_SOON, LD_COMING_SOON_EXTENSIONS, ldCoverageOf, ldFormatLabel, summarizeLdIntake } from './ldHandoff';
import { useLdBatchIngest, savedBatchId } from './useLdBatchIngest';
import Portfolio from './views/Portfolio';
// Ryan (subtask "Revisions to Framework Mapping screen"): the old four-box FrameworkMapping is
// replaced by the six-outcome / two-axis Disposition Framework (design option 4a).
import DispositionFramework from './framework/DispositionFramework';
// Item 9 (Asana 1216373781992284) — the Rule Configuration screen (spec §10.1): client tunes
// factor weights / gate toggles / box definitions / thresholds without a developer.
import RuleConfiguration from './framework/RuleConfiguration';
// Item 11 (spec §5.8, §10.3, §11) — the bulk approve/override/commit surface.
import ReviewQueue from './framework/ReviewQueue';
// Item 12 — Owner Attestation, capture layer only (question set, coverage/gaps, export/import).
import OwnerAttestation from './framework/OwnerAttestation';
// Item 15 (spec §10.4) — Rule & Decision Audit View: ruleset version history with diffs, plus the
// per-EUC decision log / portfolio-wide override log.
import AuditView from './framework/AuditView';
// Ryan (Loom "New screen: Consolidation Opportunities"): promote the existing Consolidate &
// Decommission clusters surface to its own tab between Framework Mapping and Engagement Phases.
import Clusters from './framework/Clusters';
import Phases from './views/Phases';
import ArtifactPanel from './components/ArtifactPanel';
import Toast from './components/Toast';

const VIEWS: { id: View; label: string; icon: typeof LayoutGrid }[] = [
  { id: 'portfolio', label: 'Portfolio', icon: LayoutGrid },
  { id: 'framework', label: 'Framework Mapping', icon: Grid },
  // Item 11 (spec §5.8, §10.3) — filter, bulk select, approve/override, commit.
  { id: 'review', label: 'Review Queue', icon: ListChecks },
  { id: 'consolidation', label: 'Consolidation Opportunities', icon: Combine },
  { id: 'phases', label: 'Engagement Phases', icon: Milestone },
  // Item 9 (spec §10.1) — a fourth entry point, alongside the "Adjust framework →" link on
  // the Framework Mapping screen itself.
  { id: 'rules', label: 'Rule Configuration', icon: SlidersHorizontal },
  // Item 12 — capture layer only: coverage/gaps, per-EUC answer entry, export/import, engagement
  // config (Q5/Q10 lists). No scoring wiring here — see OwnerAttestation.tsx's doc comment.
  { id: 'attestation', label: 'Owner Attestation', icon: ClipboardList },
  // Item 15 (spec §10.4) — ruleset version diffs + the append-only decision/override log.
  { id: 'audit', label: 'Rule & Decision Audit', icon: History },
];

// Handoff §3: "Persist view, selectedArtifactId, mainTab, panelTab, translateTab in the URL
// (query params) so links deep-link." Read once on mount, then keep in sync via replaceState
// so deep-linking works without the module owning the app's router.
const PARAM_KEYS = ['view', 'artifact', 'tab', 'panelTab', 'translateTab'] as const;

const STAGE_TEXT: Record<string, string> = {
  parsing: 'Parsing file…',
  extracting: 'Extracting logic',
  assembling: 'Assembling lineage…',
  scoring: 'Scoring disposition…',
  complete: 'Finishing…',
};

function stageLabel(p: IngestProgress | null): string {
  if (!p) return 'Analyzing…';
  const base = STAGE_TEXT[p.stage] || 'Analyzing…';
  if (p.stage === 'extracting' && p.total) return `${base} ${p.done}/${p.total}`;
  return base;
}

function readParams(): Partial<Record<(typeof PARAM_KEYS)[number], string>> {
  if (typeof window === 'undefined') return {};
  const q = new URLSearchParams(window.location.search);
  return Object.fromEntries(PARAM_KEYS.map((k) => [k, q.get(k) || undefined]));
}

export interface LogicDecomposerDashboardProps {
  details?: typeof artifactDetails;
  phaseData?: typeof phaseFixtures;
  // Item 4 (Migration Architect tabbed workspace): when the Migration Architect result panel
  // embeds this dashboard for its own Artifacts / Framework Mapping / Consolidation tabs, ITS tab
  // strip drives which view shows — this component's own switcher would just be a second,
  // redundant nav bar, which is exactly the "nothing is navigable" complaint item 4 is fixing.
  // Uncontrolled (the `/app/logic-decomposer` route's own use) when `view` is omitted.
  view?: View;
  hideViewSwitcher?: boolean;
  // MECE IA (task 1218070214136694, subtask 4): when embedded (controlled `view`), in-screen
  // navigation — "Adjust framework →" jumping to Rule Configuration, the Framework Mapping chips
  // jumping to Consolidation — must move the OWNER's tab, not this component's unused internal
  // state. The owner (MigrationArchitectWorkspace) passes this to switch its outer tab. When
  // omitted (standalone route), navigation falls back to this component's own setView.
  onNavigate?: (view: View) => void;
}

export default function LogicDecomposerDashboard({
  details = artifactDetails,
  phaseData = phaseFixtures,
  view: controlledView,
  hideViewSwitcher = false,
  onNavigate,
}: LogicDecomposerDashboardProps) {
  const initial = useMemo(readParams, []);
  const navigate = useNavigate();
  const location = useLocation();
  const isStandalone = location.pathname.startsWith('/app/logic-decomposer');

  // Live data from /api/framework — the same tenant ingest the Framework Mapping tab uses. The
  // Portfolio list is derived from it; nothing renders from a demo fixture. Until an inventory is
  // ingested the whole surface shows an upload prompt (no placeholder numbers).
  const live = useFrameworkData();
  const hasData = live.records.length > 0;
  // Item 14: live engagement-artifact state. `phaseData` stays an explicit override prop (tests /
  // storybook, same convention as `details` above) — when it's not passed, the live hook's phases
  // win over the phaseFixtures default the moment they load; no demo data once real data exists.
  const engagement = useEngagementArtifacts();
  const livePhaseData = engagement.phases.length ? engagement.phases : phaseData;
  const list = useMemo(() => recordsToArtifactList(live.records), [live.records]);
  // Consolidation Opportunities tab — the decommission-only queue (retiring outright, no survivor)
  // derived from the ingested records; the merge clusters come from live.clusters directly.
  const decommissionList = useMemo(() =>
    live.records
      .filter((r) => r.disposition === 'decommission')
      .map((r) => {
        const reason = (['dead', 'superseded', 'zero_consumers'] as const).find((x) => x === r.retirement_reason) || 'superseded';
        return { euc_id: r.euc_id, file: r.file, dept: r.dept, reason, detail: r.reasoning_summary || '' };
      }),
    [live.records]);
  const fileRef = useRef<HTMLInputElement>(null);

  const [uncontrolledView, setUncontrolledView] = useState<View>((initial.view as View) || 'portfolio');
  const view = controlledView ?? uncontrolledView;
  const setView = setUncontrolledView;
  // In-screen VIEW navigation (e.g. "Adjust framework →", the Framework Mapping consolidation
  // chips). When embedded (controlled), route to the owner's tab via onNavigate; otherwise drive our
  // own switcher. Using setView directly under a controlled `view` would be a silent no-op — the bug
  // MECE IA (subtask 4) fixes. Named navigateView (not `navigate`) so it never collides with the
  // react-router `navigate` used for route changes elsewhere in this component.
  const navigateView = useCallback(
    (v: View) => { if (controlledView !== undefined && onNavigate) onNavigate(v); else setView(v); },
    [controlledView, onNavigate],
  );
  const [mainTab, setMainTab] = useState<MainTab>((initial.tab as MainTab) || 'all');
  const [selectedArtifactId, setSelectedArtifactId] = useState<string | null>(initial.artifact || null);
  const [panelOpen, setPanelOpen] = useState<boolean>(!!initial.artifact);
  const [panelTab, setPanelTab] = useState<PanelTab>((initial.panelTab as PanelTab) || 'overview');
  const [translateTab, setTranslateTab] = useState<TranslateTab>((initial.translateTab as TranslateTab) || 'requirements');
  const [toast, setToast] = useState('');

  // Per-file "Analyze a file" — uploads ONE workflow file (Excel / Excel+VBA / Alteryx) to the live
  // LD analyze endpoint and opens the panel on its REAL extracted Business Logic. Distinct from the
  // inventory (.xlsx list) ingest above: that populates the portfolio roster; this decomposes a file.
  const analyzeRef = useRef<HTMLInputElement>(null);
  const analyzer = useArtifactAnalyze();
  const { currentProject, createProject } = useProject();
  // The inventory ingest carries the project so the clustering pre-pass can read that project's
  // decomposed runs for formula + output-schema overlap.
  const onInventoryFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (file) await live.ingest(file, currentProject?.id ? Number(currentProject.id) : null);
  };
  const [analyzed, setAnalyzed] = useState<Record<string, ArtifactDetail>>({});
  const [inventoryDetails, setInventoryDetails] = useState<Record<string, ArtifactDetail>>({});


  // Ryan's 7/31 QA finding 2: switching away from the Business Logic tab mid-generation
  // silently discarded the result — coming back showed "No X generated yet" and forced a full
  // re-generate. Root cause: TranslateBlock only unmounts when panelTab !== 'logic' (ArtifactPanel
  // renders it conditionally), and the translation it was holding lived in TranslateBlock's OWN
  // component state. onPersistTranslation below wrote the finished translation to the BACKEND
  // (persistTranslation, a PUT) but never back into the local artifact detail this component
  // tree reads from — so on remount, TranslateBlock re-seeded itself from artifact.translations,
  // which the local state had never updated. Two fixes, together:
  //   1. mergeTranslationLocal writes the finished translation into whichever bucket
  //      (analyzed / inventoryDetails) currently holds this artifact, so a remount picks it up
  //      without needing a network round trip.
  //   2. pendingTranslations tracks generations still in flight, keyed by `${artifactId}:${target}`,
  //      independent of whether TranslateBlock itself is mounted — so a remount mid-generation
  //      shows "Generating…" (via the `pending` prop) instead of the misleading "not generated".
  const [pendingTranslations, setPendingTranslations] = useState<Set<string>>(new Set());
  const translationKey = (id: string, target: TranslateTab) => `${id}:${target}`;
  const mergeTranslationLocal = useCallback((id: string, target: TranslateTab, translation: Translation) => {
    const merge = (d: ArtifactDetail): ArtifactDetail => ({
      ...d,
      translations: { ...(d.translations || {}), [target]: translation },
    });
    setAnalyzed((m) => (m[id] ? { ...m, [id]: merge(m[id]) } : m));
    setInventoryDetails((m) => (m[id] ? { ...m, [id]: merge(m[id]) } : m));
  }, []);
  const attachTargetRef = useRef<string | null>(null);
  const onAttach = useCallback((id: string) => {
    attachTargetRef.current = id;
    analyzeRef.current?.click();
  }, []);
  const decomposeOne = useCallback(async (file: File): Promise<ArtifactDetail | null> => {
    // Prefer the LIVE decompose (real lineage + rationale) when a project is selected; it falls
    // back to the deterministic parse if the server flag (LDX_REAL_INGEST) is off or it errors.
    if (currentProject?.id) {
      try {
        return await analyzer.ingest(file, Number(currentProject.id));
      } catch (err: any) {
        const status = err?.response?.status;
        const timedOut = err?.code === 'ECONNABORTED' || /timeout/i.test(err?.message || '');
        setToast(
          status === 501
            ? 'Live decompose is off — set LDX_REAL_INGEST=1 and restart the backend. Showing parsed logic only.'
            : timedOut
              ? 'The extraction run is still going on the server but the request timed out — showing parsed logic only.'
              : (err?.response?.data?.error || `Live decompose failed${status ? ` (${status})` : ''} — showing parsed logic only.`),
        );
        return analyzer.analyze(file);
      }
    }
    setToast('Select a project to run the live decompose — showing parsed logic only.');
    return analyzer.analyze(file);
  }, [analyzer, currentProject]);

  const onAnalyzeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    const attachTo = attachTargetRef.current;
    attachTargetRef.current = null;
    if (!file) return;
    // Source-type coverage (task 1218070214136694, subtask 3): a SOW Day-1 format we can't parse
    // yet (Access / Python / R / SQL / Tableau Prep) is skipped HONESTLY with its type named, not
    // pushed into the parser to fail three layers deep.
    const coverage = ldCoverageOf(file.name);
    if (coverage !== 'supported') {
      setToast(
        coverage === 'coming_soon'
          ? `${ldFormatLabel(file.name)} isn't parsed yet — it's on the SOW Day-1 list but no parser has been built. Skipped, not decomposed.`
          : `${ldFormatLabel(file.name)} isn't a supported source type — skipped.`,
      );
      return;
    }
    // Inventory-register guard (briefing §4; QA 2026-09-23). A register (.xlsx roster of EUC rows,
    // no per-cell logic) belongs in "Load inventory", not here — sent through Analyze it lands as a
    // bogus zero-logic EUC with no warning. Only .xlsx/.xlsm can be a register, so we probe those
    // first with the deterministic (no-LLM) parse. If it IS a register we decline to create an
    // artifact and steer to Load inventory; if it is NOT and no project is selected, we reuse this
    // same probe result as the detail rather than parsing the file a second time.
    let probed: ArtifactDetail | null = null;
    if (!attachTo && /\.xls[xm]$/i.test(file.name)) {   // .xlsx or .xlsm — the only formats a register can be
      probed = await analyzer.analyze(file);
      if (probed?.register_signal?.is_register) {
        setToast(
          `"${file.name}" looks like an EUC inventory register, not a workflow — analyzing it would create a zero-logic EUC. `
          + 'Load it with "Step 1 — Load inventory (.xlsx)" instead so its rows populate the portfolio.',
        );
        fileRef.current?.click();   // open the Load inventory picker so the fix is one click away
        return;                     // decline: no zero-logic artifact is created
      }
    }
    // Reuse the probe only for the deterministic path (no project → decomposeOne would just call
    // analyze again). With a project selected we still want the LIVE decompose, so re-run it.
    const detail = probed && !currentProject?.id ? probed : await decomposeOne(file);
    if (!detail) { setToast(analyzer.error || 'Could not analyze the file.'); return; }
    if (attachTo) {
      // Item 5 ("attach-file-to-inventory-row"): this used to merge the decomposed detail into
      // local component state only — nothing was ever sent to the backend, so the "attachment"
      // vanished on reload and overlapSignals.js's clustering pre-pass (Consolidation Signals
      // 2/3) never saw it, exactly the "154 -> 155" client-side-illusion problem item 3 fixed for
      // the Portfolio count. A real attach needs a persisted run — decomposeOne() only returns a
      // run_id when the LIVE ingest path ran (a project was selected and LDX_REAL_INGEST is on);
      // the deterministic-parse fallback has nothing durable to attach.
      if (!detail.run_id) {
        setToast('Parsed, but not attached — attaching a file to an inventory row needs the live decomposition (select a project) so the attachment can be saved.');
        return;
      }
      const pid = currentProject?.id ? Number(currentProject.id) : null;
      const res = await live.attachArtifact(attachTo, detail.run_id, pid, file.name);
      if (!res.ok) {
        setToast(`Could not attach "${file.name}": ${res.error || 'unknown error'}`);
        return;
      }
      // res.euc is the fresh, persisted DispositionRecord (attachArtifact already awaited the
      // reload that produced it) — building the panel detail from it, not from live.records right
      // after the await, avoids reading this closure's now-stale copy of that array.
      const base = res.euc ? recordToArtifactDetail(res.euc) : (inventoryDetails[attachTo] || detail);
      setInventoryDetails((m) => ({
        ...m,
        [attachTo]: {
          ...base,
          purpose: detail.purpose || base.purpose,
          sources: detail.sources,
          targets: detail.targets,
          transforms: detail.transforms,
          transform_count: detail.transform_count,
          counts: detail.counts,
          logic_rows: detail.logic_rows,
          signals: base.signals.length ? base.signals : detail.signals,
          run_id: detail.run_id,
          file_attached: true,
        },
      }));
      setSelectedArtifactId(attachTo);
      setPanelTab('logic');
      setPanelOpen(true);
      setToast(`Attached "${file.name}" to ${attachTo}.`);
      return;
    }
    setAnalyzed((m) => ({ ...m, [detail!.id]: detail! }));
    setSelectedArtifactId(detail.id);
    setPanelTab('logic');       // open straight to the real extracted logic
    setPanelOpen(true);
  };

  const [batch, setBatch] = useState<{ total: number; done: number; current: string; failed: string[] } | null>(null);
  const batchRunningRef = useRef(false);

  const runBatch = useCallback(async (queue: File[]) => {
    if (batchRunningRef.current || queue.length === 0) return;
    batchRunningRef.current = true;
    const failed: string[] = [];
    let last: ArtifactDetail | null = null;
    for (let i = 0; i < queue.length; i += 1) {
      const f = queue[i];
      setBatch({ total: queue.length, done: i, current: f.name, failed: [...failed] });
      try {
        const detail = await decomposeOne(f);
        if (detail) {
          last = detail;
          setAnalyzed((m) => ({ ...m, [detail.id]: detail }));
        } else {
          failed.push(f.name);
        }
      } catch {
        failed.push(f.name);
      }
    }
    setBatch({ total: queue.length, done: queue.length, current: '', failed });
    batchRunningRef.current = false;
    if (last) {
      setSelectedArtifactId(last.id);
      setPanelTab('logic');
      setPanelOpen(true);
    }
    if (failed.length) setToast(`${failed.length} of ${queue.length} file(s) could not be decomposed: ${failed.join(', ')}`);
    setTimeout(() => setBatch(null), 6000);
  }, [decomposeOne]);

  const handoffDrainedRef = useRef(false);
  useEffect(() => {
    if (handoffDrainedRef.current) return;
    handoffDrainedRef.current = true;
    const queued = takePendingLdFiles();
    if (queued.length) runBatch(queued);
  }, [runBatch]);

  // Files decomposed earlier for this project. A run is stored, so it comes back after a reload
  // instead of vanishing with the tab that produced it.
  const [priorRuns, setPriorRuns] = useState<DecomposedRun[]>([]);
  const refreshPriorRuns = useCallback(async () => {
    const pid = currentProject?.id;
    if (!pid) { setPriorRuns([]); return; }
    try { setPriorRuns(await listDecomposedRuns(Number(pid))); } catch { setPriorRuns([]); }
  }, [currentProject]);
  useEffect(() => {
    let live = true;
    (async () => { const pid = currentProject?.id; if (!pid) { setPriorRuns([]); return; }
      try { const rows = await listDecomposedRuns(Number(pid)); if (live) setPriorRuns(rows); }
      catch { if (live) setPriorRuns([]); }
    })();
    return () => { live = false; };
  }, [currentProject]);

  // Bulk intake — zip / folder drop, at engagement scale (Asana 1216373781992284 item 1). The
  // sequential work runs server-side (see useLdBatchIngest), so it survives a reload; on mount,
  // resume polling any batch that was still running for this project when the tab last closed.
  const ldBatch = useLdBatchIngest();

  // "New Project" (Ryan's QA finding 2026-08-03: uploaded files from unrelated past sessions —
  // 160 artifacts left over from SMBC work — were still visible while starting a new client's
  // engagement, with no way to start clean short of a hard page reload). `analyzed` /
  // `inventoryDetails` are this component's own in-memory state and never cleared on their own,
  // so anything analyzed under the OLD project kept showing up after switching projects — this
  // resets exactly that state and starts a real new project via the same ProjectContext every
  // other project-creation entry point in the app uses.
  //
  // NOTE for whoever picks up the Framework/Portfolio tenant-wide inventory next: this does NOT
  // clear the Apparity inventory ingest (framework_snapshots dispositions/clusters) — that table
  // is deliberately ONE ROW PER TENANT today (frameworkStore.js header comment: "shared across
  // the tenant's users"), not per-project, and half a dozen already-shipped features (rule
  // config, governance, attestation, decision log, overlap flag, review queue) read/write it on
  // that assumption. Splitting it per-project is a real, separate migration — flagged to Ryan,
  // not silently done here.
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [creatingProject, setCreatingProject] = useState(false);
  // POST /api/projects hard-rejects an untagged container (containerTaggingGuard —
  // "A client tag is required to create this container. Pick one from the dropdown.").
  // This modal used to send a name alone, so every create failed with an error that
  // pointed at a dropdown the modal never rendered. The tag is part of the form now.
  const [newProjectTagId, setNewProjectTagId] = useState<number | null>(null);
  const startFreshLocalState = useCallback(() => {
    setAnalyzed({});
    setInventoryDetails({});
    setSelectedArtifactId(null);
    setPanelOpen(false);
    if (ldBatch.batch || ldBatch.error) ldBatch.dismiss();
  }, [ldBatch]);

  // Round 2 UX fix — delete one document from the Portfolio list. Two kinds of row share this
  // list (see extraRows/portfolioList above): a "run:<id>" row is a locally/prior-decomposed file
  // that hasn't been folded into the persisted framework inventory yet — there is no framework
  // disposition on the backend to delete for it, so this just clears it out of local view state
  // (analyzed / inventoryDetails / priorRuns). Anything else is a real euc_id backed by a
  // framework_project_snapshots row — live.deleteArtifact removes that row server-side too.
  const handleDeleteArtifact = useCallback(async (id: string) => {
    if (id.startsWith('run:')) {
      setAnalyzed((m) => { if (!(id in m)) return m; const next = { ...m }; delete next[id]; return next; });
      setInventoryDetails((m) => { if (!(id in m)) return m; const next = { ...m }; delete next[id]; return next; });
      setPriorRuns((rows) => rows.filter((r) => `run:${r.run_id}` !== id));
      if (selectedArtifactId === id) { setSelectedArtifactId(null); setPanelOpen(false); }
      return;
    }
    const res = await live.deleteArtifact(id);
    if (res.ok) {
      if (selectedArtifactId === id) { setSelectedArtifactId(null); setPanelOpen(false); }
    } else {
      setToast(`Delete failed: ${res.error || 'error'}`);
    }
  }, [live, selectedArtifactId]);
  // Both the button and the Enter key gate on this — a name on its own can never
  // produce a project, so offering the action would only reproduce the old failure.
  const canCreateNewProject = Boolean(newProjectName.trim()) && newProjectTagId !== null;
  const handleCreateNewProject = async () => {
    const name = newProjectName.trim();
    if (!name || newProjectTagId === null || creatingProject) return;
    setCreatingProject(true);
    try {
      const ok = await createProject({ name, client_tag_id: newProjectTagId });
      if (ok) {
        startFreshLocalState();
        setNewProjectOpen(false);
        setNewProjectName('');
        // Cleared rather than kept: the picker's autoSelectLastUsed re-seeds it from
        // the tag this create just recorded, so the next project opens pre-filled.
        setNewProjectTagId(null);
        setToast(`Started "${name}" — previous project's uploaded files are cleared from this view.`);
      }
    } finally {
      setCreatingProject(false);
    }
  };
  const zipRef = useRef<HTMLInputElement>(null);
  const folderRef = useRef<HTMLInputElement>(null);
  const batchResumedRef = useRef<number | null>(null);
  useEffect(() => {
    const pid = currentProject?.id ? Number(currentProject.id) : null;
    if (!pid || batchResumedRef.current === pid) return;
    batchResumedRef.current = pid;
    const saved = savedBatchId(pid);
    if (saved) ldBatch.resume(saved, pid);
  }, [currentProject, ldBatch]);
  const batchWasRunningRef = useRef(false);
  useEffect(() => {
    if (ldBatch.batch?.status === 'running') batchWasRunningRef.current = true;
    if (ldBatch.batch?.status === 'done' && batchWasRunningRef.current) {
      batchWasRunningRef.current = false;
      refreshPriorRuns();
      const failedCount = ldBatch.batch.files.filter((f) => f.status === 'error').length;
      const skippedCount = ldBatch.batch.skipped.length;
      const parts = [`Batch complete: ${ldBatch.batch.done - failedCount} of ${ldBatch.batch.total} decomposed`];
      if (failedCount) parts.push(`${failedCount} failed`);
      if (skippedCount) parts.push(`${skippedCount} skipped (unsupported format)`);
      setToast(parts.join(' — '));
    }
  }, [ldBatch.batch, refreshPriorRuns]);
  const startBulkUpload = useCallback((files: File[], archive?: File) => {
    const pid = currentProject?.id ? Number(currentProject.id) : null;
    if (!pid) { setToast('Select a project before running a bulk upload.'); return; }
    ldBatch.start({ files, archive, projectId: pid });
  }, [currentProject, ldBatch]);
  const onZipSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (file) startBulkUpload([], file);
  };
  const onFolderSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const all = Array.from(e.target.files || []);
    const picked = all.filter((f) => isLdParsable(f.name));
    e.target.value = '';
    // Source-type coverage (task 1218070214136694, subtask 3): don't just filter and forget —
    // count what's skipped by coverage so the consultant sees "18 supported, 6 coming soon,
    // 2 unsupported" instead of a silent drop that reads as "we decomposed everything."
    const summary = summarizeLdIntake(all.map((f) => f.name));
    const skipNote = summary.comingSoon || summary.unsupported
      ? ` Skipped ${summary.comingSoon + summary.unsupported} (${summary.comingSoon} coming-soon, ${summary.unsupported} unsupported).`
      : '';
    if (picked.length === 0) {
      setToast(`No parsable workflow files in that folder.${skipNote} Supported: Excel, Alteryx, DataStage, Informatica, Tableau workbooks.`);
      return;
    }
    if (skipNote) setToast(`Ingesting ${picked.length} supported file${picked.length === 1 ? '' : 's'}.${skipNote}`);
    startBulkUpload(picked);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const q = new URLSearchParams(window.location.search);
    q.set('view', view);
    q.set('tab', mainTab);
    q.set('panelTab', panelTab);
    q.set('translateTab', translateTab);
    if (selectedArtifactId) q.set('artifact', selectedArtifactId);
    else q.delete('artifact');
    window.history.replaceState(null, '', `${window.location.pathname}?${q.toString()}`);
  }, [view, mainTab, panelTab, translateTab, selectedArtifactId]);

  const [loadingRun, setLoadingRun] = useState<string | null>(null);
  const openArtifact = useCallback(async (id: string) => {
    if (!analyzed[id] && !details[id] && !inventoryDetails[id]) {
      // A row from an earlier session: pull the stored run back before opening the panel.
      const prior = priorRuns.find((r) => `run:${r.run_id}` === id);
      if (prior && currentProject?.id) {
        setLoadingRun(id);
        try {
          const detail = await loadDecomposedRun(prior.run_id, Number(currentProject.id));
          setAnalyzed((m) => ({ ...m, [id]: detail }));
          setPanelTab('overview');
        } catch {
          setToast('Could not re-open that decomposition run.');
          return;
        } finally {
          setLoadingRun(null);
        }
      } else {
        const rec = live.records.find((r) => (r.euc_id || '') === id);
        if (!rec) { setToast('No disposition record behind this row yet.'); return; }
        const base = recordToArtifactDetail(rec);
        // Item 5: file_attached is now real (recordsToPortfolio.ts reads rec.artifacts), so a row
        // that HAS an attached run should actually show its lineage/logic, not just claim to have
        // one. The EUC-level record only carries the scorecard, not per-run lineage, so the most
        // recently attached run's real detail is fetched here — best-effort: if it fails, the
        // panel still opens on the scorecard-only base rather than blocking the click.
        if (rec.artifacts && rec.artifacts.length > 0 && currentProject?.id) {
          const latest = rec.artifacts[rec.artifacts.length - 1];
          setLoadingRun(id);
          try {
            const runDetail = await loadDecomposedRun(latest.run_id, Number(currentProject.id));
            setInventoryDetails((m) => ({
              ...m,
              [id]: {
                ...base,
                purpose: runDetail.purpose || base.purpose,
                sources: runDetail.sources,
                targets: runDetail.targets,
                transforms: runDetail.transforms,
                transform_count: runDetail.transform_count,
                counts: runDetail.counts,
                logic_rows: runDetail.logic_rows,
                run_id: runDetail.run_id,
              },
            }));
          } catch {
            setToast('This row has an attached file, but its extracted detail could not be loaded.');
            setInventoryDetails((m) => ({ ...m, [id]: base }));
          } finally {
            setLoadingRun(null);
          }
        } else {
          setInventoryDetails((m) => ({ ...m, [id]: base }));
        }
        setPanelTab('overview');
      }
    }
    setSelectedArtifactId(id);
    setPanelOpen(true);
  }, [details, analyzed, inventoryDetails, live.records, priorRuns, currentProject]);

  // Handoff §5.3: clicking an artifact inside the Framework detail card jumps to Portfolio /
  // EUC Inventory with the panel open on it. Uses `navigateView` (not `setView`) so it moves the
  // OWNER's tab when embedded in MA — a raw setView would be a silent no-op under a controlled
  // `view` (the MECE-IA subtask-4 no-op class).
  const openFromFramework = useCallback((id: string) => {
    navigateView('portfolio');
    openArtifact(id);
  }, [navigateView, openArtifact]);

  const selected = selectedArtifactId
    ? (analyzed[selectedArtifactId] || inventoryDetails[selectedArtifactId] || details[selectedArtifactId])
    : undefined;
  const detailById = useCallback(
    (id: string) => analyzed[id] || inventoryDetails[id] || details[id],
    [analyzed, inventoryDetails, details],
  );

  // Analyzed files become Portfolio rows (prepended) so an uploaded file is visible in the list and
  // stays clickable — not just a one-shot panel that vanishes when closed.
  const detailToRow = (d: ArtifactDetail): ArtifactRow => {
    const c = d.counts;
    const pct = Math.min(100, (c.total_statements || 0) * 8);
    const complexity: Complexity = pct >= 66 ? 'High' : pct >= 33 ? 'Medium' : 'Low';
    return {
      id: d.id,
      name: d.name,
      meta_line: `Uploaded file · ${c.formulas} formulas · ${c.hardcodes} hardcodes`,
      owner: d.owner,
      assignee: d.assignee,
      timing_cat: d.timing_cat,
      outcome: d.outcome,
      disposition: d.disposition,
      target_state_label: d.target_state_label,
      confidence: d.confidence || null,
      complexity,
      complexity_pct: pct,
      status: 'Pending',
    };
  };
  // Every file put through "Analyze a file" is also filed by the backend as its own EUC row
  // (eucGrouping.createEucFromRun, called from the ingest route). That row is what `list`
  // renders and what Framework Mapping counts, so rendering the run as well showed the same
  // file twice in the Portfolio and left the Portfolio total one ahead of Framework Mapping's
  // "all N EUCs" heading for every analyzed file — the 17-vs-16 split QA pinned.
  //
  // A run is dropped only once a record has actually claimed it. Auto-create is best-effort and
  // logs a warning rather than failing the ingest, so an unclaimed run must still be listed or a
  // decomposed file would vanish from the view entirely. Nothing is lost by hiding the claimed
  // one: openArtifact fetches that same run's lineage and logic for the record row (see above),
  // so the surviving row carries the scorecard AND the extracted detail.
  const claimedRunIds = useMemo(
    () => new Set(live.records.flatMap((r) => (r.artifacts || []).map((a) => a.run_id))),
    [live.records],
  );
  const runIdOf = (rowId: string) => rowId.replace(/^run:/, '');

  const analyzedRows = Object.values(analyzed)
    .filter((d) => !claimedRunIds.has(runIdOf(d.id)))
    .map(detailToRow);

  // Rows for runs decomposed in an earlier session. Skipped once the run has been re-opened,
  // so a file never appears twice.
  const priorRows: ArtifactRow[] = priorRuns
    .filter((r) => !analyzed[`run:${r.run_id}`] && !claimedRunIds.has(r.run_id))
    .map((r) => {
      const d = r.disposition || {};
      const conf = typeof d.confidence === 'number' ? d.confidence : null;
      const tier = String(d.risk_tier || '').toLowerCase();
      const complexity: Complexity = tier === 'high' ? 'High' : tier === 'medium' ? 'Medium' : 'Low';
      const when = r.scored_at ? new Date(r.scored_at).toLocaleDateString() : '';
      const outcome = outcomeFromRaw(d.disposition);
      return {
        id: `run:${r.run_id}`,
        name: r.source_file,
        meta_line: `Decomposed${when ? ` ${when}` : ''}${r.parser_id ? ` · ${r.parser_id}` : ''}`,
        owner: 'Uploaded file',
        assignee: '—',
        timing_cat: 3 as const,
        outcome,                                       // single taxonomy
        disposition: OUTCOME_TO_LEGACY[outcome],        // legacy alias (export only)
        target_state_label: d.target_state ? String(d.target_state).replace(/_/g, ' ') : undefined,
        confidence: conf,
        complexity,
        complexity_pct: conf != null ? Math.round(conf * 100) : 0,
        status: 'Pending' as const,
      };
    });

  const extraRows = [...analyzedRows, ...priorRows];
  // Ryan's 7/31 QA finding 4: an uploaded file that lands as an extraRow (an "Analyze a file"
  // upload or a prior decomposed run not yet folded into the persisted inventory) filtered
  // correctly — Portfolio.tsx's own row-level filter reads portfolioList.items, which already
  // includes extraRows — but its tab badge stayed at 0 and the header stayed on the pre-upload
  // count. Both read a DIFFERENT field that this object used to leave untouched: counts_by_
  // disposition and total were spread straight from the base `list` (the persisted 154/155
  // inventory only), never recomputed to include extraRows. Tally extraRows into a fresh
  // counts_by_disposition here so every number on the screen — the filtered rows, the tab
  // badges, and the header count — comes from the same object instead of two that can drift.
  const portfolioList = extraRows.length
    ? {
        ...list,
        items: [...extraRows, ...list.items],
        total: list.total + extraRows.length,
        counts_by_outcome: extraRows.reduce((acc, r) => {
          acc[r.outcome] = (acc[r.outcome] || 0) + 1;
          return acc;
        }, { ...list.counts_by_outcome }),
      }
    : list;

  // The ribbon's confirmed/in-review split is in the mockup but not in any §4 contract, so
  // there is nothing to read it from yet. Hard-coding the mockup's numbers would put two
  // invented figures next to a real one; deriving them from status keeps the ribbon honest
  // as soon as real rows arrive. Reads portfolioList (not list) for the same reason as above —
  // an uploaded file's row must count here too, not just in the base inventory.
  const autoConfirmed = portfolioList.items.filter((r) => r.status === 'Approved').length;
  const inReview = portfolioList.items.filter((r) => r.status === 'Pending').length;

  // Shared empty state — shown on Portfolio / Engagement Phases until an inventory is ingested.
  const emptyPrompt = (
    <div style={{ maxWidth: 560, margin: '32px auto', textAlign: 'center', background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: 20, padding: '40px 32px' }}>
      {live.loading ? (
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: color.ink }}>Loading…</h2>
      ) : (
        <>
          <Upload size={22} style={{ color: color.textMuted, marginBottom: 10 }} />
          <h2 style={{ margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: color.ink }}>Start the EUC intake</h2>
          {/* Single intake path (task 1218070214136694, subtask 7): two explicit steps, one place.
              Step 1 loads the inventory (populates every Tier-1 row); step 2 attaches source files
              to those rows (the "Analyze a file" / .zip / folder controls in the header). No demo
              numbers ever show here — the screen stays empty until the tenant's real ingest lands. */}
          <p style={{ fontSize: 13.5, color: color.textMuted, margin: '0 0 8px' }}>
            <b>Step 1.</b> Load the Apparity EUC inventory (.xlsx) — this populates every Tier-1 row across the portfolio, the framework plan, and the deliverables.
          </p>
          <p style={{ fontSize: 13.5, color: color.textMuted, margin: '0 0 20px' }}>
            <b>Step 2.</b> Attach source files to rows (Analyze a file · .zip · folder, above) where the client provided them. Unsupported formats are listed, never dropped silently.
          </p>
          <button onClick={() => fileRef.current?.click()} disabled={live.ingesting} style={{
            cursor: live.ingesting ? 'default' : 'pointer', fontSize: 13.5, fontWeight: 700, color: '#fff',
            background: color.accent, border: 'none', borderRadius: 11, padding: '11px 20px',
          }}>
            {live.ingesting ? 'Loading…' : 'Step 1 — Load inventory (.xlsx)'}
          </button>
          {/* No input here: the fileRef file input lives once in the header (below) so it stays
              mounted whether or not this empty state is shown. This button reuses that same ref. */}
        </>
      )}
    </div>
  );

  return (
    <div style={{ fontFamily: font.ui, background: color.appBg, minHeight: '100%', position: 'relative' }}>
      <div style={{ padding: '28px 32px', maxWidth: 1400, margin: '0 auto' }}>
        {isStandalone && (
          <button
            onClick={() => navigate('/app/intelligence')}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              background: 'transparent', border: 'none', padding: 0, marginBottom: 12,
              fontSize: 12.5, fontWeight: 600, color: color.textMuted, fontFamily: font.ui,
            }}
          >
            ← Back to Jarvis Intelligence
          </button>
        )}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'nowrap' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: color.ink, letterSpacing: '-0.01em' }}>
                Logic Decomposer
              </h1>
              {currentProject && (
                <span style={{
                  fontSize: 12, fontWeight: 700, color: color.textMuted, background: color.surface,
                  border: `1px solid ${color.cardBorder}`, borderRadius: 999, padding: '3px 10px',
                }}>
                  {currentProject.name}
                </span>
              )}
              <button
                onClick={() => setNewProjectOpen(true)}
                title="Start a new project — clears uploaded files from this view so a new client's work doesn't mix with the last one"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
                  fontSize: 12, fontWeight: 700, color: color.accent, background: 'transparent',
                  border: `1px solid ${color.accent}`, borderRadius: 999, padding: '3px 10px',
                }}
              >
                + New project
              </button>
            </div>
            <p style={{ margin: '6px 0 0', fontSize: 14, color: color.textMuted }}>
              Portfolio disposition across the EUC population — what to migrate, document, decommission or queue.
            </p>
          </div>
          {/* All intake controls grouped so they sit together on one line, rather than being spread
              across the header by the parent's space-between (which left a large gap between
              "Load inventory" and "Analyze a file"). marginLeft:auto pins the group to the right of
              the title; nowrap + flex-shrink:0 keep the buttons on a single row (they never drop
              below the title). */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'nowrap', marginLeft: 'auto', flexShrink: 0 }}>
          {/* Register import — the Apparity EUC inventory (.xlsx). The empty state has a prominent
              "Step 1 — Load inventory" hero, but once any artifact exists the empty state is gone;
              without this header control the register-import path would survive only on Framework
              Mapping, and a consultant who analyzed a file first could not find it on Portfolio and
              might push the register through "Analyze a file" — which §4 warns against (QA 2026-09-23).
              A quiet, always-present control keeps the inventory-vs-analyze distinction reachable at
              all times without competing with the empty state's prominent Step 1 CTA. Uses the same
              fileRef / onInventoryFile as the empty state (the input now lives in the header so it is
              mounted whether or not the empty state is shown). */}
          <div>
            <button
              onClick={() => fileRef.current?.click()}
              disabled={live.ingesting}
              title={hasData
                ? 'Reload the Apparity EUC inventory workbook (.xlsx)'
                : 'Load the Apparity EUC inventory workbook (.xlsx) — populates every Tier-1 row'}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: live.ingesting ? 'default' : 'pointer',
                fontSize: 13.5, fontWeight: 700, color: color.ink, background: color.surface,
                border: `1px solid ${color.cardBorder}`, borderRadius: 11, padding: '11px 18px', whiteSpace: 'nowrap',
              }}
            >
              {live.ingesting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {live.ingesting ? 'Loading…' : (hasData ? 'Reload inventory' : 'Load inventory (.xlsx)')}
            </button>
            <input ref={fileRef} type="file" accept=".xlsx" onChange={onInventoryFile} style={{ display: 'none' }} />
          </div>
          {/* Per-file decomposition — upload one workflow file (Excel / Excel+VBA / Alteryx) and see
              its real extracted logic in the panel. Runs against the live analyze endpoint (§4.2). */}
          <div>
            <button
              onClick={() => analyzeRef.current?.click()}
              disabled={analyzer.analyzing}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: analyzer.analyzing ? 'default' : 'pointer',
                fontSize: 13.5, fontWeight: 700, color: '#fff', background: color.accent, border: 'none',
                borderRadius: 11, padding: '11px 18px', whiteSpace: 'nowrap',
              }}
            >
              <Upload size={14} />
              {analyzer.analyzing ? stageLabel(analyzer.progress) : 'Analyze a file'}
            </button>
            <input
              ref={analyzeRef}
              type="file"
              accept={LD_ACCEPT_WITH_COMING_SOON}
              onChange={onAnalyzeFile}
              style={{ display: 'none' }}
            />
          </div>
          {/* Bulk intake — zip or folder, at engagement scale (50-200 artifacts/session). Runs
              server-side (useLdBatchIngest) so it survives a tab close mid-run. */}
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <button
              onClick={() => zipRef.current?.click()}
              disabled={ldBatch.starting}
              title="Upload a .zip containing workflow files"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: ldBatch.starting ? 'default' : 'pointer',
                fontSize: 13.5, fontWeight: 700, color: color.ink, background: color.surface,
                border: `1px solid ${color.cardBorder}`, borderRadius: 11, padding: '11px 18px', whiteSpace: 'nowrap',
              }}
            >
              {ldBatch.starting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              Upload .zip
            </button>
            <button
              onClick={() => folderRef.current?.click()}
              disabled={ldBatch.starting}
              title="Upload every workflow file in a folder"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, cursor: ldBatch.starting ? 'default' : 'pointer',
                fontSize: 13.5, fontWeight: 700, color: color.ink, background: color.surface,
                border: `1px solid ${color.cardBorder}`, borderRadius: 11, padding: '11px 18px', whiteSpace: 'nowrap',
              }}
            >
              {ldBatch.starting ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {ldBatch.starting ? `Uploading${ldBatch.startingCount ? ` ${ldBatch.startingCount}` : ''}…` : 'Upload folder'}
            </button>
            <input ref={zipRef} type="file" accept=".zip" onChange={onZipSelect} style={{ display: 'none' }} />
            <input
              ref={folderRef}
              type="file"
              // @ts-expect-error — webkitdirectory is a real, widely-supported attribute with no
              // React/TS typing; it's what turns this into a folder picker instead of a file picker.
              webkitdirectory=""
              multiple
              onChange={onFolderSelect}
              style={{ display: 'none' }}
            />
          </div>
          </div>
        </div>

        {/* Source-type coverage matrix (task 1218070214136694, subtask 3). Supported = the parsers
            that actually run; coming-soon = SOW Day-1 formats named but not yet parseable (Access,
            Python, R, SQL, Tableau Prep). Listed honestly so a consultant knows exactly which of
            the client's Day-1 artifacts will decompose and which are skipped — never a silent drop. */}
        <div style={{ margin: '8px 0 0', fontSize: 12, color: color.textMuted, lineHeight: 1.6 }}>
          <div>
            <b style={{ color: color.ink }}>Supported now:</b>{' '}
            Excel (formulas / macros), Alteryx, DataStage, Informatica, Tableau workbooks.
          </div>
          {LD_COMING_SOON_EXTENSIONS.length > 0 && (
            <div>
              <b style={{ color: color.ink }}>Coming soon</b> (SOW Day-1, no parser yet):{' '}
              Access ({['.mdb', '.accdb'].join(' / ')}), Python (.py), R (.r), SQL (.sql), Tableau Prep (.tfl).
              These files are listed and skipped, not decomposed.
            </div>
          )}
        </div>

        {ldBatch.starting && (
          <div style={{
            marginTop: 18, background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: 14,
            padding: '12px 16px', fontSize: 13, color: color.ink, display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <Loader2 size={16} className="animate-spin" style={{ color: color.accent, flexShrink: 0 }} />
            <span>
              Uploading{ldBatch.startingCount ? ` ${ldBatch.startingCount} file${ldBatch.startingCount === 1 ? '' : 's'}` : ''} to the server —
              this can take a moment for a large folder. Don't close this tab.
            </span>
          </div>
        )}

        {ldBatch.error && (
          <div style={{
            marginTop: 18, background: '#FEF2F2', border: '1px solid #FCA5A5', borderRadius: 14,
            padding: '12px 16px', fontSize: 13, color: '#991B1B', display: 'flex', justifyContent: 'space-between', gap: 12,
          }}>
            <span>{ldBatch.error}</span>
            <button onClick={ldBatch.dismiss} style={{ color: '#991B1B', fontWeight: 700 }}>Dismiss</button>
          </div>
        )}

        {ldBatch.batch && (
          <div style={{
            marginTop: 18, background: color.surface, border: `1px solid ${color.cardBorder}`,
            borderRadius: 14, padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: color.ink }}>
                {ldBatch.batch.status === 'running'
                  ? `Decomposing ${ldBatch.batch.done} of ${ldBatch.batch.total}`
                  : `Batch complete — ${ldBatch.batch.done} of ${ldBatch.batch.total} file(s) processed`}
              </span>
              <span style={{ color: color.textMuted }}>
                {ldBatch.batch.total > 0 ? Math.round((ldBatch.batch.done / ldBatch.batch.total) * 100) : 0}%
              </span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: color.appBg, overflow: 'hidden' }}>
              <div style={{
                width: `${ldBatch.batch.total > 0 ? (ldBatch.batch.done / ldBatch.batch.total) * 100 : 0}%`, height: '100%',
                background: color.accent, transition: 'width 240ms ease',
              }} />
            </div>
            {ldBatch.batch.skipped.length > 0 && (
              <div style={{ fontSize: 12, color: color.textMuted, marginTop: 8 }}>
                Skipped ({ldBatch.batch.skipped.length}, unsupported format): {ldBatch.batch.skipped.slice(0, 5).map((s) => s.name).join(', ')}
                {ldBatch.batch.skipped.length > 5 ? ` +${ldBatch.batch.skipped.length - 5} more` : ''}
              </div>
            )}
            {ldBatch.batch.files.some((f) => f.status === 'error') && (
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 8 }}>
                Failed: {ldBatch.batch.files.filter((f) => f.status === 'error').map((f) => f.name).join(', ')}
              </div>
            )}
            {ldBatch.batch.status !== 'running' && (
              <button onClick={ldBatch.dismiss} style={{ marginTop: 10, fontSize: 12, color: color.textMuted, textDecoration: 'underline' }}>
                Dismiss
              </button>
            )}
          </div>
        )}

        {batch && (
          <div style={{
            marginTop: 18, background: color.surface, border: `1px solid ${color.cardBorder}`,
            borderRadius: 14, padding: '14px 16px',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, marginBottom: 8 }}>
              <span style={{ fontWeight: 700, color: color.ink }}>
                {batch.done < batch.total
                  ? `Decomposing ${batch.done + 1} of ${batch.total} — ${batch.current}`
                  : `Decomposed ${batch.total - batch.failed.length} of ${batch.total} file(s)`}
              </span>
              <span style={{ color: color.textMuted }}>{Math.round((batch.done / batch.total) * 100)}%</span>
            </div>
            <div style={{ height: 6, borderRadius: 999, background: color.appBg, overflow: 'hidden' }}>
              <div style={{
                width: `${(batch.done / batch.total) * 100}%`, height: '100%',
                background: color.accent, transition: 'width 240ms ease',
              }} />
            </div>
            {batch.failed.length > 0 && batch.done === batch.total && (
              <div style={{ fontSize: 12, color: '#B45309', marginTop: 8 }}>
                Failed: {batch.failed.join(', ')}
              </div>
            )}
          </div>
        )}

        {/* Status ribbon — only once an inventory is ingested, and hidden on the Framework Mapping
            view (which has its own "N EUCs" hero) to avoid two populations on one screen. */}
        {hasData && view !== 'framework' && view !== 'rules' && view !== 'review' && view !== 'attestation' && view !== 'audit' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 18, margin: '14px 0 24px', color: '#4a4770', fontSize: 13.5, flexWrap: 'wrap' }}>
            <div><b style={{ color: color.ink }}>{portfolioList.total}</b> artifacts</div>
            <span style={{ color: '#c9c8d9' }}>·</span>
            <div><b style={{ color: color.ink }}>{autoConfirmed}</b> auto-confirmed</div>
            <span style={{ color: '#c9c8d9' }}>·</span>
            <div><b style={{ color: '#d97706' }}>{inReview}</b> in review</div>
            <span style={{ color: '#c9c8d9' }}>·</span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: '#16a34a' }} />
              Ready for review
            </div>
          </div>
        )}

        {/* View switcher — suppressed when an embedding parent (item 4's Migration Architect
            workspace) already renders its own tab strip and is driving `view` directly. */}
        {!hideViewSwitcher && (
        <div style={{ display: 'inline-flex', gap: 4, background: '#eceef7', padding: 4, borderRadius: 12, marginBottom: 26 }}>
          {VIEWS.map((v) => {
            const active = view === v.id;
            const Icon = v.icon;
            return (
              <button
                key={v.id}
                onClick={() => setView(v.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '9px 16px', border: 'none', borderRadius: 9,
                  fontFamily: font.ui, fontSize: 13.5, fontWeight: 600, cursor: 'pointer',
                  background: active ? color.surface : 'transparent',
                  color: active ? color.ink : color.textMuted,
                  boxShadow: active ? '0 1px 3px #0d0b2b18' : 'none',
                }}
              >
                <Icon size={14} />
                {v.label}
              </button>
            );
          })}
        </div>
        )}

        {/* Portfolio + Engagement Phases read the ingested inventory. Until it lands they show one
            clear upload call-to-action — never a demo set. Framework Mapping owns its own state. */}
        {view === 'portfolio' && ((hasData || extraRows.length > 0) ? (
          <Portfolio
            list={portfolioList}
            mainTab={mainTab}
            onMainTab={setMainTab}
            selectedArtifactId={panelOpen ? selectedArtifactId : null}
            onOpenArtifact={openArtifact}
            onDeleteArtifact={handleDeleteArtifact}
          />
        ) : emptyPrompt)}
        {view === 'framework' && (
          <DispositionFramework
            onAdjustFramework={() => navigateView('rules')}
            onNavigateConsolidation={() => navigateView('consolidation')}
          />
        )}
        {view === 'review' && <ReviewQueue onToast={setToast} />}
        {view === 'attestation' && <OwnerAttestation />}
        {view === 'audit' && <AuditView />}
        {view === 'rules' && <RuleConfiguration />}
        {view === 'consolidation' && (hasData ? (
          <div>
            {/* General rationale (Ryan's Loom): how clusters are determined, in plain English.
                The per-cluster "Chosen because" + signals below give the specific drill-in rationale. */}
            <div style={{ background: color.surface, border: `1px solid ${color.cardBorder}`, borderRadius: 16, padding: '16px 18px', marginBottom: 20 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: color.ink, marginBottom: 6 }}>How these clusters are determined</div>
              <p style={{ margin: 0, fontSize: 13, color: color.textMuted, lineHeight: 1.6 }}>
                Files are grouped when they share the same <b>data sources</b>, <b>formulas / logic</b>, <b>intended purpose</b> and <b>audience</b>. Where overlap is high, one file becomes the <b>survivor</b> and the rest fold into it — collapsing several files into one. Open any cluster for the specific rationale on why those files group together.
              </p>
            </div>
            <Clusters
              clusters={live.clusters}
              decommission={decommissionList}
              nearMisses={live.nearMisses}
              onOverrideSurvivor={live.overrideSurvivor}
            />
          </div>
        ) : emptyPrompt)}
        {view === 'phases' && (hasData ? <Phases phases={livePhaseData} onToast={setToast} download={engagement.download} /> : emptyPrompt)}
      </div>

      {panelOpen && selected && (
        <ArtifactPanel
          artifact={selected}
          panelTab={panelTab}
          onPanelTab={setPanelTab}
          translateTab={translateTab}
          onTranslateTab={setTranslateTab}
          onClose={() => setPanelOpen(false)}
          onToast={setToast}
          // Tracked outside TranslateBlock so a generation survives the component unmounting
          // when the user switches panel tabs mid-run (Ryan 7/31 QA finding 2) — see the
          // pendingTranslations comment above.
          pendingTranslation={pendingTranslations.has(translationKey(selected.id, translateTab))}
          fetchTranslation={async (id, target) => {
            const key = translationKey(id, target);
            setPendingTranslations((s) => new Set(s).add(key));
            try {
              return await translateDetail(detailById(id), target);
            } finally {
              setPendingTranslations((s) => {
                if (!s.has(key)) return s;
                const next = new Set(s);
                next.delete(key);
                return next;
              });
            }
          }}
          reviewTranslation={(id, target, current) => reviewDetail(detailById(id), target, current)}
          onAttach={onAttach}
          // Single-EUC override (task 1218070214136694, subtask 8): reuse the SAME engine endpoint
          // the Framework Mapping worked-example and the Review Queue use (live.overrideDisposition
          // → PUT /dispositions/:id/override) — no duplicated commit logic. Only offered for a real
          // inventory row (an euc id present in live.records), not an ad-hoc analyzed upload.
          onOverride={
            live.records.some((r) => r.euc_id === selected.id)
              ? (eucId, outcome, reason) => live.overrideDisposition(eucId, outcome, reason)
              : undefined
          }
          onPersistTranslation={(runId, target, translation) => {
            if (currentProject?.id) persistTranslation(runId, Number(currentProject.id), target, translation);
            // Local-first: the artifact detail this tree reads from must carry the finished
            // translation too, not just the backend — otherwise a remounted TranslateBlock
            // (panel tab switch, or close/reopen) re-seeds from a stale artifact.translations
            // and shows "not generated" even though the run actually finished. See comment above.
            mergeTranslationLocal(selected.id, target, translation);
          }}
        />
      )}

      {newProjectOpen && (
        <div
          role="dialog"
          aria-modal="true"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(15,15,25,0.45)', zIndex: 1000,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
          }}
          onClick={() => { if (!creatingProject) setNewProjectOpen(false); }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: color.surface, borderRadius: 14, padding: 22, width: 380,
              maxWidth: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
            }}
          >
            <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 800, color: color.ink }}>New project</h3>
            <p style={{ margin: '0 0 14px', fontSize: 12.5, color: color.textMuted, lineHeight: 1.5 }}>
              Starts a fresh Logic Decomposer session for a new client or engagement — the files and
              runs you've uploaded here won't carry over.
            </p>
            <input
              autoFocus
              value={newProjectName}
              onChange={(e) => setNewProjectName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCreateNewProject(); }}
              placeholder="Project name (e.g. the client's name)"
              disabled={creatingProject}
              style={{
                width: '100%', boxSizing: 'border-box', fontSize: 13.5, padding: '9px 11px',
                borderRadius: 9, border: `1px solid ${color.cardBorder}`, marginBottom: 12, fontFamily: font.ui,
              }}
            />
            <div style={{ marginBottom: 16 }}>
              <ClientTagPicker
                value={newProjectTagId}
                onChange={setNewProjectTagId}
                required
                autoSelectLastUsed
                disabled={creatingProject}
                label="Client Engagement"
                helpText="Every project is tagged so its usage can be attributed to the right engagement."
              />
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                onClick={() => setNewProjectOpen(false)}
                disabled={creatingProject}
                style={{
                  fontSize: 12.5, fontWeight: 700, color: color.textMuted, background: 'none',
                  border: 'none', cursor: creatingProject ? 'default' : 'pointer', padding: '9px 12px',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleCreateNewProject}
                disabled={creatingProject || !canCreateNewProject}
                style={{
                  fontSize: 12.5, fontWeight: 700, color: '#fff',
                  background: canCreateNewProject ? color.accent : color.textMuted,
                  border: 'none', borderRadius: 9, padding: '9px 16px',
                  cursor: creatingProject || !canCreateNewProject ? 'default' : 'pointer',
                }}
              >
                {creatingProject ? 'Creating…' : 'Create & start fresh'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Toast message={toast} onClear={() => setToast('')} />
    </div>
  );
}
